import { WalletPayment } from '@aaa-ncnu-ie/mypolicy-api-billing';
import { Stack, Typography, Button } from '@mui/material';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useWalletQuery } from '../../api/wallet/getWallet';
import Add from '../../assets/icons/Add';
import colors from '../../constants/colors';
import useAnalytics from '../../hooks/useAnalytics';
import useGetCustKey from '../../hooks/useGetCustKey';
import useUpdateTitle from '../../hooks/useUpdateTitle';
import { useSnackbarsContext } from '../../store/SnackbarProvider';
import { getWalletAccount, getWalletCard } from '../../utils/wallet';
import PaymentMethodOptionButton from './PaymentMethodOptionButton';
import CircularProgress from '../../components/CircularProgress/CircularProgress';

const WalletPage = () => {
  useUpdateTitle('Payment Methods');
  const { track } = useAnalytics();
  const location = useLocation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbarsContext();
  const custKey = useGetCustKey();
  const { data: wallet, isLoading, error } = useWalletQuery(custKey);
  const walletAccounts: WalletPayment[] = wallet?.accounts ?? [];

  const walletEmpty = error || walletAccounts.length === 0;

  useEffect(() => {
    if (location.state?.alertOpen) {
      window.history.replaceState({}, document.title);
      enqueueSnackbar({
        message: location.state?.message,
        className: 'csaa-qm-block',
        autoHideDuration: 10000,
        variant: 'success',
        key: 'paymentMethodSnackbar',
      });
    }
  }, [location.state?.alertOpen]);

  useEffect(() => {
    return () => {
      closeSnackbar('paymentMethodSnackbar');
    };
  }, []);

  if (isLoading) {
    return (
      <Stack
        sx={{
          alignItems: "center",
          justifyContent: "center",
          width: 1
        }}>
        <CircularProgress size={120} />
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: { mobile: 'center', desktop: 'flex-start' },
        width: 1
      }}>
      <Stack
        data-testid="walletPage"
        sx={{
          width: 1,
          minHeight: '100%',
          px: { mobile: 2, desktop: 19.5 },
          pt: { mobile: 3, desktop: 9 },
          pb: 3,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: {
              mobile: 33,
              tablet: 44,
            },
            lineHeight: {
              mobile: '48px',
              tablet: '56px',
              desktop: '56px',
            },
            letterSpacing: {
              mobile: 0.5,
              tablet: 0.5,
              desktop: 0.6,
            },
            fontWeight: {
              mobile: 400,
              tablet: 500,
            },
            color: colors.csaaBlack,
            mb: 1,
          }}
        >
          Manage payment methods
        </Typography>
        <Typography
          sx={{
            fontSize: {
              mobile: 23,
              tablet: 28,
            },
            lineHeight: {
              mobile: '32px',
              tablet: '32px',
              desktop: '40px',
            },
            letterSpacing: {
              mobile: 0.4,
              tablet: 0.4,
              desktop: 0.6,
            },
            fontWeight: {
              mobile: 400,
              tablet: 500,
            },
            color: colors.csaaDark,
            mb: { mobile: 2, tablet: 3 },
          }}
        >
          Set up and manage payment methods for your policy.
        </Typography>
        <Stack
          spacing={2}
          sx={{
            backgroundColor: colors.csaaWhite,
            borderRadius: 2,
            py: { mobile: 3, tablet: 5 },
            px: { mobile: 2, tablet: 5 },
            boxShadow: '0px 4px 8px 0px rgba(25, 30, 43, 0.08)',
          }}
        >
          <Stack direction="column">
            <Stack
              direction="row"
              sx={{
                justifyContent: { mobile: 'center', tablet: 'flex-end' },
                px: 2,
                py: 1
              }}>
              <Button
                component={Link}
                startIcon={<Add />}
                to="/wallet/add"
                sx={{
                  textTransform: 'none',
                  fontSize: { mobile: 16, tablet: 18 },
                  fontWeight: 700,
                  lineHeight: '24px',
                  letterSpacing: { mobile: 0.3, tablet: 0.4 },
                  color: colors.csaaSpectrumBlue50,
                }}
                onClick={() => {
                  track('Payment Methods Modified', {
                    location: 'Feature Card',
                    event_type: 'Option Selected',
                    link: 'Add payment method',
                  });
                }}
                disableRipple
              >
                Add payment method
              </Button>
            </Stack>
          </Stack>
          {walletEmpty ? (
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontSize: 16,
                fontWeight: 400,
              }}
            >
              You currently have no payment methods saved in MyPolicy.
            </Typography>
          ) : (
            walletAccounts.map((account: WalletPayment, idx) => (
              <PaymentMethodOptionButton
                account={account}
                key={
                  (account.shortName ??
                    getWalletAccount(account)?.number ??
                    getWalletCard(account)?.number) + idx
                }
              />
            ))
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default WalletPage;
